import type { FC } from "react";
import { useState } from "react";

import type { AsyncAvData } from "@ui/hooks/useSessionAsyncAv";
import { useTranslations } from "@ui/providers/useTranslations";
import dompurify from "isomorphic-dompurify";

import { Dialog } from "../Dialog";
import { FormProvider, useForm } from "../Form";
import { type AvUserDataType, ValidationMethods } from "../ValidationMethods";
import type { AgeVerificationNavigation } from "../ValidationMethods/hooks/useAgeVerificationFlow";
import type { AvFormValues } from "../ValidationMethods/interfaces/AvFormValues";

import styles from "./AgeVerificationDialog.module.scss";

const { sanitize } = dompurify;

export type AgeVerificationDialogProps = {
  isOpened?: boolean;
  isPending?: boolean;
  description: string;
  userData?: AvUserDataType;
  onSuccess?: (avpUuid?: string) => void;
  onLoadDataFromCache?: (asyncAvData: AsyncAvData) => void;
  onFail?: () => void;
  onClose: () => void;
  portalDomNode?: HTMLElement;
  ageVerificationMethodImages?: Record<string, string>;
  navigation: AgeVerificationNavigation;
};

export const AgeVerificationDialog: FC<AgeVerificationDialogProps> = ({
  onClose,
  onSuccess,
  onFail,
  onLoadDataFromCache,
  isPending,
  userData,
  description,
  isOpened = true,
  portalDomNode,
  ageVerificationMethodImages,
  navigation,
}) => {
  const form = useForm<AvFormValues>({
    defaultValues: {
      avpValues: {},
    },
  });
  const { __ } = useTranslations();
  const [selectedMethod, setSelectedMethod] = useState("");
  const [verificationMethodImage, setVerificationMethodImage] = useState("");
  const { __: translate } = useTranslations();

  const onSelectMethod = (method: string) => {
    setSelectedMethod(method);
    setVerificationMethodImage(ageVerificationMethodImages?.[method] ?? "");
  };

  return (
    <FormProvider {...form}>
      <Dialog isOpened={isOpened} onClose={onClose} closeStyle={styles.closeIcon} portalDomNode={portalDomNode}>
        <div data-testid="ageVerificationDialogContent" className={styles.container}>
          <h3 className={styles.headline}>{translate("ageverification.dialog.headline")}</h3>
          <p className={styles.textBlock} dangerouslySetInnerHTML={{ __html: sanitize(description) }}></p>
          <ValidationMethods
            onLoadDataFromCache={onLoadDataFromCache}
            onSuccess={onSuccess}
            onFail={onFail}
            isPending={isPending}
            userData={userData}
            selectedMethodImage={verificationMethodImage}
            selectMethod={onSelectMethod}
            selectedMethod={selectedMethod}
            buttonClassName={styles.submitButton}
            navigation={navigation}
          />
        </div>
      </Dialog>
    </FormProvider>
  );
};
